<template>
    <div class="grafico-container">
        <div class="grafico-header">
            <h3 class="grafico-titulo">Custo por Projeto</h3>
            <GraficoInfoIcon 
                titulo="Custo por Projeto"
                descricao="Mostra o custo total de cada projeto, ajudando a identificar projetos com maior investimento."
            />
        </div>
        <div v-if="!temDados" class="sem-dados">Nenhum dado disponível</div>
        <Bar v-else :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import { Bar } from 'vue-chartjs';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import GraficoInfoIcon from './GraficoInfoIcon.vue';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

export default {
    name: 'GraficoCustoPorProjeto',
    components: { Bar, GraficoInfoIcon },
    props: {
        projetos: {
            type: Array,
            default: () => []
        }
    },
    emits: ['clicar-projeto'],
    methods: {
        formatarNomeProjeto(projeto, maxLength = 25) {
            const nome = projeto.nome || 'Sem nome';
            const codigoPCM = projeto.pcm?.codigo || '';
            const nomeCompleto = codigoPCM ? `${codigoPCM} - ${nome}` : nome;
            return nomeCompleto.length > maxLength ? nomeCompleto.substring(0, maxLength) + '...' : nomeCompleto;
        }
    },
    computed: {
        projetosComCusto() {
            return this.projetos
                .filter(p => (parseFloat(p.custo_total) || 0) > 0)
                .sort((a, b) => parseFloat(b.custo_total || 0) - parseFloat(a.custo_total || 0))
                .slice(0, 20);
        },
        temDados() {
            return this.projetosComCusto.length > 0;
        },
        chartData() {
            const projetos = this.projetosComCusto;
            return {
                labels: projetos.map(p => this.formatarNomeProjeto(p)),
                datasets: [{
                    label: 'Custo (R$)',
                    data: projetos.map(p => parseFloat(p.custo_total || 0)),
                    backgroundColor: 'rgba(54, 117, 211, 0.6)',
                    borderColor: 'rgba(54, 117, 211, 1)',
                    borderWidth: 2
                }]
            };
        },
        chartOptions() {
            const self = this;
            return {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: 'y',
                onClick: (event, elements) => {
                    if (elements.length > 0) {
                        const index = elements[0].index;
                        const projeto = this.projetosComCusto[index];
                        if (projeto) {
                            self.$emit('clicar-projeto', projeto.id);
                        }
                    }
                },
                scales: {
                    x: {
                        beginAtZero: true,
                        title: {
                            display: true,
                            text: 'Custo (R$)'
                        },
                        ticks: {
                            callback: (value) => {
                                if (value >= 1000000) return 'R$ ' + (value / 1000000).toFixed(1) + 'M';
                                if (value >= 1000) return 'R$ ' + (value / 1000).toFixed(0) + 'k';
                                return 'R$ ' + value;
                            }
                        }
                    },
                    y: {
                        title: {
                            display: true,
                            text: 'Projetos'
                        }
                    }
                },
                plugins: {
                    legend: {
                        display: false
                    },
                    tooltip: {
                        callbacks: {
                            label: (context) => {
                                const valor = context.parsed.x;
                                return 'Custo: ' + new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
                            },
                            footer: () => 'Clique para ver detalhes'
                        }
                    }
                }
            };
        }
    }
};
</script>

<style scoped>
.grafico-container {
    background: var(--cor-bg, white);
    border: 1px solid var(--cor-separador, #ddd);
    border-radius: 8px;
    padding: 1.5rem;
    height: 600px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
}

.grafico-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.grafico-titulo {
    margin: 0;
    font-size: 1.2rem;
    font-weight: 600;
    color: var(--cor-fonte, #333);
}

.sem-dados {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #999;
}
</style>

